import { Link } from "react-router-dom";
import { useConfig } from "@/lib/api";
import { NAV_ITEMS } from "@/components/nav/TopNav";
import { Annotation } from "@/components/ui-custom/Annotation";
import { DrawLine } from "@/components/ui-custom/Reveal";

const Col = ({ title, children }) => (
  <div>
    <p className="mono-label mb-4 text-ink/45">{title}</p>
    <div className="flex flex-col gap-2 text-[14px] leading-relaxed">{children}</div>
  </div>
);

const FLink = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="link-line inline-block w-fit text-ink/80 hover:text-ink">
    {children}
  </Link>
);

export const MonumentFooter = () => {
  const { data: cfg } = useConfig();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative bg-paper text-ink">
      <div className="container-x pt-20 pb-8 sm:pt-28">
        <DrawLine />
        <div className="flex items-end justify-between pt-4">
          <Annotation>{cfg?.descriptor || "Generalbauunternehmung"}</Annotation>
          <Annotation>{cfg?.address?.country || "Schweiz"}</Annotation>
        </div>

        <div className="mt-10 flex justify-start" data-testid="footer-logo">
          <img
            src="/brand/la-logo-dark.webp"
            alt={cfg?.legal_name || "L&A Projektbau GmbH"}
            width={1191}
            height={474}
            loading="lazy"
            draggable={false}
            className="block h-auto w-full max-w-[180px] select-none opacity-80"
          />
        </div>

        <div className="mt-16 grid grid-cols-2 gap-10 border-t hl pt-10 md:grid-cols-3 lg:mt-24">
          <Col title="Navigation">
            {NAV_ITEMS.map((n) => (
              <FLink key={n.to} to={n.to} testId={`footer-link-${n.label.toLowerCase()}`}>
                {n.label}
              </FLink>
            ))}
            <FLink to="/industrie" testId="footer-link-industrie">Industrie & Service</FLink>
          </Col>
          <Col title="Kontakt">
            {cfg?.email && (
              <a href={`mailto:${cfg.email}`} className="link-line w-fit text-ink/80" data-testid="footer-email">{cfg.email}</a>
            )}
            {cfg?.phone && !cfg.phone_is_placeholder && (
              <a href={`tel:${cfg.phone.replace(/\s/g, "")}`} className="link-line w-fit text-ink/80" data-testid="footer-phone">{cfg.phone}</a>
            )}
            {cfg?.linkedin && (
              <a href={cfg.linkedin} target="_blank" rel="noreferrer" className="link-line w-fit text-ink/80">
                LinkedIn
              </a>
            )}
            <FLink to="/kontakt" testId="footer-project-request-link">Projekt anfragen</FLink>
          </Col>
          <Col title="Adresse">
            {cfg ? (
              <address className="not-italic text-ink/80">
                {cfg.legal_name}
                <br />
                {cfg.address.street}
                <br />
                {cfg.address.zip} {cfg.address.city}
              </address>
            ) : (
              <span className="text-ink/50">–</span>
            )}
            <span className="mono-sm text-ink/45">{cfg?.coordinates?.dms}</span>
          </Col>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t hl pt-5 sm:flex-row sm:items-center sm:justify-between" data-testid="footer-legal-bar">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Annotation className="text-ink/45">
              © {year} {cfg?.legal_name || "L&A Projektbau GmbH"}
            </Annotation>
            <Link to="/impressum" data-testid="footer-link-impressum" className="link-line font-heading text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/70 hover:text-ink">
              Impressum
            </Link>
            <Link to="/datenschutz" data-testid="footer-link-datenschutz" className="link-line font-heading text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/70 hover:text-ink">
              Datenschutz
            </Link>
          </div>
          <Annotation className="text-ink/70" dot>
            Build <span className="opacity-40">/</span> Coordinate <span className="opacity-40">/</span> Deliver
          </Annotation>
        </div>
      </div>
    </footer>
  );
};
